import React, { useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { Redirect, useHistory } from "react-router-dom";
import {
  URI_PATHS,
  API_ROUTES,
  IS_MOBILE,
  INVALID_EMAIL_ERROR_MESSAGE,
  GENERIC_ERROR_MESSAGE,
  HTTP_BAD_REQUEST_ERROR_CODE,
  ONBOARDING_VERSION_2_0,
} from "../../../constants";
import { updateToNextStep } from "../../../state/actions";

/* Custom Hooks */
import { useQueryParams, usePostApi } from "../../../state/store/customHooks";

/* Components */
import Error from "../errors/Error";
import BasicInfoDesktop from "./BasicInfoDesktop";
import BasicInfoMobile from "./BasicInfoMobile";
const NOT_ACCEPTING_NEW_APPLICATIONS = "We are not currently accepting signups at this time";
const WAITLIST_API_ERROR_MESSAGE = "You have been added to the waitlist. We will reach out shortly.";

const BasicInfoV2 = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const loggedIn = localStorage.getItem("jwt");

  const queryParams = useQueryParams();
  const queryParamsEmail = queryParams.get("user");
  const email = queryParamsEmail && queryParamsEmail.toLowerCase();
  const referralCode = queryParams.get("referral");

  // api
  const [userExistsApi, {
    loading: userExistsApiLoading,
    error: userExistsApiError,
    data: userExistsApiData,
  }] = usePostApi(API_ROUTES.USER_EXISTS);

  const userApiPayload = {
    userId: email,
    configVersion: ONBOARDING_VERSION_2_0,
    source: "CARD_ONBOARDING", // TODO: Figure out why common-fe imports are broken for onboarding
  };
  const [newUserRecordApi, {
    loading: newUserApiLoading,
    error: newUserApiError,
  }] = usePostApi(API_ROUTES.NEW_USER_RECORD);
  const [newUserSignupApi, { loading: newUserSignupApiLoading }] = usePostApi(API_ROUTES.UPDATE_USER_BASIC_INFO);

  const [userExists, setUserExists] = useState(false);
  const [passwordExists, setPasswordExists] = useState(false);
  const [createUserError, setCreateUserError] = useState(null);

  // helper functions
  const _completeStep = useCallback(async (payload) => {

    if(loggedIn)
      return dispatch(updateToNextStep(history));

    let res;
    try {
      res = await newUserSignupApi({ payload });  // api depends on sending an obj that has 'payload'...
    } catch (err) {
      const { status: httpStatus, data } = err;
      if (httpStatus === HTTP_BAD_REQUEST_ERROR_CODE) {
        if (data?.message === WAITLIST_API_ERROR_MESSAGE) {
          setCreateUserError(NOT_ACCEPTING_NEW_APPLICATIONS);
        } else {
          setCreateUserError(INVALID_EMAIL_ERROR_MESSAGE);
        }
      }
      else {
        setCreateUserError(GENERIC_ERROR_MESSAGE);
      }

      return null;
    }

    const { token: jwt } = res || {};

    localStorage.setItem("jwt", jwt);
    dispatch(updateToNextStep(history));

    return null;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  const _initializeStep = useCallback(() => {
    if(loggedIn) {
      return;
    }
    
    if(!userExistsApiData) {
      userExistsApi(userApiPayload);
      return;
    }

    if(userExistsApiError)
      return;

    const { userExists: userExistsData, passwordExists: passwordExistsData } = userExistsApiData;
      
    setUserExists(userExistsData);
    setPasswordExists(passwordExistsData);

    if(!userExistsData && !passwordExistsData) {
      newUserRecordApi(userApiPayload);
    }

  }, [userApiPayload, userExistsApiData, userExistsApiError]);

  useEffect(() => {
    _initializeStep();

    // we only need to run this effect twice.
    // Once on mount and one more time after userExistsApi goes through
  }, [userExistsApiData, userExistsApiError]);

  // only need to send them to login page if they're attempting to manually enter an
  // email that already exists. Otherwise, we want to show Read only state
  if((!email && !loggedIn) || (userExists && passwordExists)) {
    return <Redirect to={URI_PATHS.LOGIN} />;
  }
    

  if(userExistsApiError || newUserApiError)
    return <Error />;

  const layoutProps = {
    loading: userExistsApiLoading || newUserApiLoading || newUserSignupApiLoading,
    email,
    referralCode,
    _completeStep,
    createUserError,
  };

  if (loggedIn) {
    return (<Redirect to={URI_PATHS.RESUME_APPLICATION} />)
  }

  return (IS_MOBILE ?
    // Disable prop spreading linter rule here since we can easily see where
    // the properties of layoutProps exist
    // eslint-disable-next-line react/jsx-props-no-spreading
    <BasicInfoMobile {...layoutProps} />
    :
    // eslint-disable-next-line react/jsx-props-no-spreading
    <BasicInfoDesktop {...layoutProps} />
  );
};

export default BasicInfoV2;